// src/pages/seasons/RsvpShortLink.jsx
// Public short-link resolver for SMS RSVP links.
//
// Route: /r/:token
//
// SMS bodies carry ~38-char URLs like https://app.getscrimly.com/r/aB3xK9q
// instead of the 84-char full form. This component reads the
// `rsvpLinks/{token}` doc written by functions/modules/rsvpShortLinks.js
// and client-side redirects to /game-rsvp/{gameId}/{registrationId},
// where the existing public RSVP flow takes over unchanged.
//
// No auth required — the token IS the access credential (same security
// model as the full /game-rsvp URL with registrationId).

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/firebase';

export default function RsvpShortLink() {
  const { token }  = useParams();
  const navigate   = useNavigate();
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!token) { setErr('Missing link token.'); return; }

    let cancelled = false;
    getDoc(doc(db, 'rsvpLinks', token))
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          setErr('This link is no longer valid.');
          return;
        }
        const { gameId, regId } = snap.data() || {};
        if (!gameId || !regId) {
          setErr('This link is malformed.');
          return;
        }
        // `replace: true` so the short URL doesn't clutter history.
        navigate(`/game-rsvp/${gameId}/${regId}`, { replace: true });
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e?.message || 'Something went wrong opening this link.');
      });

    return () => { cancelled = true; };
  }, [token, navigate]);

  // Quick lime/pitch surface — this page is shown for ~200ms while the
  // resolver fetches the doc + redirects, so most parents never see it.
  // The error state is what they actually see if the link is bad. Keep
  // it consistent with RegisterPage's status screens.
  const HEAD_FONT = "'Barlow Condensed', sans-serif";
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#f7faf5]">
      <div className="max-w-sm w-full text-center">
        {err ? (
          <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-8">
            <p
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              RSVP link
            </p>
            <h1
              className="mt-1 text-lg font-bold uppercase tracking-tight text-[#0d1f14] mb-2"
              style={{ fontFamily: HEAD_FONT }}
            >
              Link not available
            </h1>
            <p className="text-sm text-zinc-600">{err}</p>
            <p className="text-xs text-zinc-500 mt-4">
              Please contact your coach for an updated link.
            </p>
          </div>
        ) : (
          <p className="text-sm text-zinc-500">Opening your RSVP…</p>
        )}
      </div>
    </div>
  );
}
